import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';

export default function CustomDatePickerWithTextField(props) {
  const [startValue, setStartValue] = React.useState(null);
  const [endValue, setEndValue] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!props?.filter) {
      setStartValue(null);
      setEndValue(null);
    }
  }, [props?.filter]);

  // Sync with external value prop
  React.useEffect(() => {
    if (props.value !== undefined) {
      if (props.from && props.value !== startValue) {
        setStartValue(props.value ? new Date(props.value) : null);
      }
      if (props.to && props.value !== endValue) {
        setEndValue(props.value ? new Date(props.value) : null);
      }
    }
  }, [props.value]);

  const safelyCallHandleDateChange = (date, isFrom, isTo, isCleared = false) => {
    if (props?.handleDateChange) {
      props.handleDateChange({
        "date": date,
        "from": isFrom !== undefined ? isFrom : false,
        "to": isTo !== undefined ? isTo : false,
        "cleared": isCleared
      });
    }
  };

  const handleChange = (newValue) => {
    if (props?.from) {
      setStartValue(newValue);
      safelyCallHandleDateChange(newValue, props?.from, props?.to);
    }
    if (props?.to) {
      setEndValue(newValue);
      safelyCallHandleDateChange(newValue, props?.from, props?.to);
    }
  };

  const handleClear = (event) => {
    if (event) event.stopPropagation();
    
    if (props?.from) {
      setStartValue(null);
      safelyCallHandleDateChange(null, true, false, true);
    }
    if (props?.to) {
      setEndValue(null);
      safelyCallHandleDateChange(null, false, true, true);
    }
    setOpen(false);
  };

  const handleToday = (event) => {
    if (event) event.stopPropagation();
    
    if (props?.from) {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      setStartValue(todayStart);
      safelyCallHandleDateChange(todayStart, props?.from, props?.to);
    }
    if (props?.to) {
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      setEndValue(todayEnd);
      safelyCallHandleDateChange(todayEnd, props?.from, props?.to);
    }
    setOpen(false);
  };

  const ActionBar = React.forwardRef((props, ref) => {
    return (
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0'
        }}
        ref={ref}
      >
        <Button
          onClick={handleClear}
          startIcon={<ClearIcon />}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Clear
        </Button>
        <Button
          onClick={handleToday}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Today
        </Button>
      </Stack>
    );
  });

  const getCurrentValue = () => {
    if (props.from) return startValue;
    if (props.to) return endValue;
    return null;
  };

  // Format the date for display in the input field
  const getFormattedDate = () => {
    const currentValue = getCurrentValue();
    if (!currentValue) return '';
    try {
      return format(currentValue, 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        inputFormat="dd/MM/yyyy"
        value={getCurrentValue()}
        onChange={handleChange} // Fixed the typo from 'o(newValue)'
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        components={{
          ActionBar: ActionBar
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            sx={{ backgroundColor: "white" }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarMonthIcon 
                    fontSize="small" 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpen(true)}
                  />
                </InputAdornment>
              ),
            }}
            placeholder={props.placeholder || "Search or select date"}
            variant="outlined"
            onClick={() => setOpen(true)}
            value={getFormattedDate()}
          />
        )}
      />
    </LocalizationProvider>
  );
}

// Usage example:
// <CustomDatePickerWithTextField
//   from={true}
//   handleDateChange={(dateInfo) => console.log(dateInfo)}
//   placeholder="Search by date"
//   label="Date Filter"
// />